// @flow
import React, { useCallback, useEffect, useState } from 'react';
import Drawer from '@material-ui/core/Drawer';
import {
  CreationSavedSearchDrawer,
  SavedSearchDrawer,
  SearchApplied,
  useSavedSearches
} from 'features/saved-searches';
import useDatatable, {
  buildRowActionsRendererDefinition,
  extractObjectFromDataTable,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import { useUrlParamsUpdater } from 'hooks/urlParamsUpdater';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { userHasPermission, userHasRoles } from 'services/Authorization';
import { When } from 'UI/components/atoms';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import DataTable from 'UI/components/organisms/DataTable';
import ListingSummary from 'UI/components/organisms/ListingSummary';
import SendoutManager from 'UI/components/organisms/sendouts';
import FiltersLayout from 'UI/components/templates/FiltersLayout';
import { additionalPeriodRanges } from 'UI/components/templates/SideFiltersLayout/filters';
import { backNavigateListConfig } from 'UI/constants/config';
import { PageTitles, RowActions } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { createCustomStaticRanges } from 'UI/utils';
import { sendoutsFormatResponse } from 'UI/utils/inventory';

import {
  columnsSendouts,
  CustomScopeFilters,
  FiltersGroup,
  FiltersInclude,
  SendoutDateFilters,
  SendoutPeriodColumnBySection,
  SendoutsExtraFilters,
  SendoutSortColumnBySection,
  SendoutTrackingDateFilter
} from './columns';
import { navigateToSendout } from './utils';

const sendoutKey = 'sendout';

const Sendouts = (props: RouterProps) => {
  const { history, location } = props;
  const initialDateFilter = SendoutTrackingDateFilter.id;
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const isSavedSearchesEnabled = checkIfFeatureFlagEnabled(FeatureFlags.SendoutsSavedSearches);

  const {
    activeSearch,
    getSavedSearchButtonDefinitions,
    handleApplySearch,
    handleCloseSavedSearchDrawer,
    handleCloseSavedSearchForm,
    handleDeleteSearch,
    handleEditSavedSearchDrawerOpen,
    handleEditSearch,
    handleRemoveSearch,
    isDirty,
    savedSearchesUiState
  } = useSavedSearches({ preferencesKey: sendoutKey });

  const orderByOptions = {
    column: SendoutSortColumnBySection,
    direction: 'desc'
  };
  const initialPreferences = getColumnPreferences(
    sendoutKey,
    0,
    orderByOptions,
    columnsSendouts,
    1,
    location.search
  );

  const checkIsVisible = ({ roles = [], permissions = [] }) => {
    const hasRole = !roles || userHasRoles(roles);
    const hasPermission =
      !permissions || permissions?.some(permission => userHasPermission(permission));
    return hasRole || hasPermission;
  };

  const allowedScopeFilters = CustomScopeFilters.filter(scopeFilter => checkIsVisible(scopeFilter));

  const { id: defaultFilter } = last(allowedScopeFilters);

  const [dateTypeFilter, setDateTypeFilter] = useState(
    SendoutDateFilters.find(type => type.id === initialDateFilter)
  );

  const {
    data,
    getData,
    listState,
    count,
    columnPreferences,
    filters,
    queryParams,
    handleFiltersApply,
    handleOneFilterChange,
    handleFiltersChange,
    handleFiltersToggle,
    handleKeywordChange,
    handleColumnSortChange,
    handlePerPageChange,
    handlePageChange,
    handlePeriodChange,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleSectionChange
  } = useDatatable({
    columnsDefinitions: columnsSendouts,
    customScopeFilters: allowedScopeFilters,
    enableSavedSearches: isSavedSearchesEnabled && savedSearchesUiState.isSearching,
    endpoint: Endpoints.Sendouts,
    entityName: 'Sendouts',
    entityType: EntityType.Sendouts,
    initialPreferences,
    initialScopeFilter: defaultFilter,
    initialSection: initialDateFilter,
    key: sendoutKey,
    numberOfActionColumns: 1,
    orderByOptions,
    periodFilterColumn: SendoutPeriodColumnBySection,
    sharePreferencesBetweenSections: true,
    shouldScrollOnNavigate: true,
    savedSearch: isSavedSearchesEnabled && activeSearch && activeSearch?.search,
    formatResponse: sendoutsFormatResponse
  });

  useEffect(() => {
    handleEditSearch(filters);
  }, [filters, handleEditSearch]);

  const { showWholeSkeleton, isLoading, isSideMenuOpen, page, perPage, userFilter, keyword } =
    listState;
  const { columns, columnOrder, orderBy, direction } = columnPreferences;
  useUrlParamsUpdater({ history, location, orderBy });

  const [uiState, setUiState] = useState({
    shouldRefresh: false,
    sendoutId: null
  });

  useEffect(() => {
    document.title = PageTitles.Sendouts;
  }, []);

  const handleViewSendout = async ({ rowData }) => {
    if (!rowData) return;
    const item = extractObjectFromDataTable(columnsSendouts, ['id'], rowData);

    setUiState(prev => ({
      ...prev,
      sendoutId: item.id
    }));
  };

  const handleCloseSendout = () =>
    setUiState(prev => ({
      ...prev,
      sendoutId: null
    }));

  const handleSendoutUpdated = async () => {
    setUiState(prev => ({
      ...prev,
      shouldRefresh: true
    }));
    await getData();
  };

  const handleSummaryLoaded = useCallback(() => {
    setUiState(prev => ({
      ...prev,
      shouldRefresh: false
    }));
  }, []);

  const handleDateTypeFilterChange = (name, value) => {
    setDateTypeFilter(value);
    handleSectionChange && handleSectionChange(name, value.id);
  };

  const actionButton = buildRowActionsRendererDefinition({
    namespace: EntityType.Sendouts,
    actions: [{ name: RowActions.View, onClick: handleViewSendout }]
  });

  const finalColumns = getColumnsToRender([...columns, actionButton]);
  const isUserFilterEnabled = allowedScopeFilters.length > 1;
  const handleSendoutNavigation = navigateToSendout(history);

  const savedSearchButtonDefinitions = getSavedSearchButtonDefinitions({ filters });

  return (
    <FiltersLayout
      filters={filters}
      title="Sendouts"
      section="sendouts"
      isSideMenuOpen={isSideMenuOpen}
      includeFilters={FiltersInclude}
      groups={FiltersGroup}
      extraFiltersDefinition={SendoutsExtraFilters}
      extraSelector={
        <AutocompleteSelect
          name="dateTypeFilter"
          placeholder="Filter by"
          selectedValue={dateTypeFilter}
          onSelect={handleDateTypeFilterChange}
          defaultOptions={SendoutDateFilters}
          disableClearable
        />
      }
      dateRangerPickerProps={{
        staticRanges: createCustomStaticRanges(additionalPeriodRanges)
      }}
      listSelector={
        <AutocompleteSelect
          name="userFilter"
          placeholder="Sendouts to Show"
          disabled={!isUserFilterEnabled}
          selectedValue={userFilter}
          onSelect={handleOneFilterChange}
          defaultOptions={allowedScopeFilters}
          disableClearable
        />
      }
      onSearch={handleFiltersApply}
      onFiltersChange={handleFiltersChange}
      onMenuToggle={handleFiltersToggle}
      onPeriodChange={handlePeriodChange}
      enableStore={false}
      titleLabelProps={backNavigateListConfig}
      enableSavedSearches={isSavedSearchesEnabled}
      savedSearchesHeader={
        isSavedSearchesEnabled &&
        activeSearch && (
          <SearchApplied
            isDirty={isDirty}
            onRemoveSearch={handleRemoveSearch}
            onSaveSearch={!isEmpty(filters) ? handleEditSavedSearchDrawerOpen : null}
            searchName={activeSearch?.name}
          />
        )
      }
      savedSearchesActions={
        isSavedSearchesEnabled && (
          <FPButtonBuilder
            GridContainerProps={{ justify: 'flex-end', spacing: 2 }}
            buttonDefinitions={savedSearchButtonDefinitions}
          />
        )
      }
    >
      <ListingSummary
        endpoint={Endpoints.SendoutsSummary}
        queryParams={queryParams.params}
        shouldRefresh={uiState.shouldRefresh}
        onDataLoaded={handleSummaryLoaded}
        numberOfColumns={3}
      />
      <DataTable
        isExpandable
        columnOrder={columnOrder?.length ? columnOrder : undefined}
        columns={finalColumns}
        count={count}
        data={data}
        draggableColumns={{ enabled: true }}
        enableCustomFilters
        filter={false}
        loading={showWholeSkeleton}
        onColumnDisplayClick={handleColumnDisplayChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnSortChange={handleColumnSortChange}
        onPageClick={handlePageChange}
        onPerPageClick={handlePerPageChange}
        onSearchTextChange={handleKeywordChange}
        onToggleFilters={handleFiltersToggle}
        page={page}
        refreshing={isLoading}
        rowsPerPage={perPage}
        searchText={keyword}
        selectableRows="none"
        sortOrder={{ name: orderBy, direction }}
      />

      <Drawer open={!!uiState.sendoutId} anchor="right" onClose={handleCloseSendout}>
        {uiState.sendoutId && (
          <SendoutManager
            onEdit={handleSendoutUpdated}
            onDelete={handleSendoutUpdated}
            onClose={handleCloseSendout}
            id={uiState.sendoutId}
            onNavigate={handleSendoutNavigation}
          />
        )}
      </Drawer>

      <When condition={isSavedSearchesEnabled && savedSearchesUiState.isSavedSearchDrawerOpen}>
        <SavedSearchDrawer
          activeSearch={activeSearch}
          entityType={EntityType.Sendout}
          onApplySearch={handleApplySearch}
          onClose={handleCloseSavedSearchDrawer}
          onDeleteSearch={handleDeleteSearch}
          open={savedSearchesUiState.isSavedSearchDrawerOpen}
        />
      </When>

      <When
        condition={
          isSavedSearchesEnabled &&
          (savedSearchesUiState.isCreationSavedSearchDrawerOpen ||
            savedSearchesUiState.isEditSavedSearchDrawerOpen)
        }
      >
        <CreationSavedSearchDrawer
          activeSearch={activeSearch}
          entityType={EntityType.Sendout}
          onApplySearch={handleApplySearch}
          onSaveCompleted={handleCloseSavedSearchForm}
          onClose={handleCloseSavedSearchForm}
          open
          savedSearchId={savedSearchesUiState.isEditSavedSearchDrawerOpen ? activeSearch?.id : null}
          search={filters}
        />
      </When>
    </FiltersLayout>
  );
};

export default Sendouts;
